import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { filter, map, Subject, takeUntil } from 'rxjs';
import { AxiosError } from 'axios';
import {
  CasesService,
  GetCardPaymentStatus,
  PaymentStatus,
} from 'src/app/services/cases/cases.service';
import { ClientsService } from 'src/app/services/clients/clients.service';
import { RootForm } from 'src/app/services/form/form.controls';
import {
  CLIENT_FORM_NAME,
  DEBTOR_FORM_NAME,
} from 'src/app/services/form/form.factory';
import { productTypes } from 'src/app/services/form/form.resources';
import {
  Case,
  CLIENT_FORM_STEP_NAME,
  DEBTOR_FORM_STEP_NAME,
  FormService,
  FormStepName,
  formStepNames,
  nextFormStep,
  PAYMENT_FORM_STEP_NAME,
  PAYMENT_REDIRECT_FORM_STEP_NAME,
} from 'src/app/services/form/form.service';
import { HelpersService } from 'src/app/services/helpers/helpers.service';
import { UsersService } from 'src/app/services/users/users.service';
import { AuthService } from 'src/app/shared/services/auth/auth.service';
import { errorResponse } from 'src/types/error-response';
import { z } from 'zod';
import { LoadingScreenComponent } from '../../shared/loading-screen/loading-screen.component';
import { FormHeaderComponent } from './form-header/form-header.component';
import { MatIcon } from '@angular/material/icon';
import { PayeeAutosaveComponent } from '../../shared/payee-autosave/payee-autosave.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { CaseFormTutorialComponent } from './case-form-tutorial/case-form-tutorial.component';
import { NgComponentOutlet } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { FooterComponent } from '../../shared/footer/footer.component';

export const caseNotDraftError = z.object({
  code: z.literal('case-not-draft'),
  uuid: z.string(),
  payee_case_reference_id: z.string(),
  gtm_start_sent_at: z.string().nullable(),
  client_id: z.string(),
  partner: z.string().nullable(),
  product_type: z.enum(productTypes),
  origin: z.string(),
});

@Component({
  selector: 'app-case-form',
  templateUrl: './case-form.component.html',
  styleUrls: ['./case-form.component.scss'],
  imports: [
    LoadingScreenComponent,
    FormHeaderComponent,
    MatIcon,
    PayeeAutosaveComponent,
    MatProgressSpinner,
    CaseFormTutorialComponent,
    NgComponentOutlet,
    MatButton,
    FooterComponent,
    TranslateModule,
  ],
})
export class CaseFormComponent implements OnInit, OnDestroy {
  private casesService = inject(CasesService);
  private clientsService = inject(ClientsService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private formService = inject(FormService);
  private helpersService = inject(HelpersService);
  private authService = inject(AuthService);
  private usersService = inject(UsersService);
  private translate = inject(TranslateService);
  private snackbar = inject(MatSnackBar);
  readonly saving = toSignal(
    this.formService.saving.pipe(map((value) => value > 0)),
    { initialValue: false },
  );

  private readonly destroy = new Subject<void>();
  private canShowSpinnerTimeout?: NodeJS.Timeout;
  private caseId?: string;
  step?: FormStepName;

  get form(): RootForm {
    return this.formService.form;
  }
  get formComponents() {
    return this.formService.getFormComponents();
  }
  get case(): Case {
    return this.formService.case!;
  }
  readonly canShowSpinner = signal(false);
  readonly showLoadingScreen = signal(true);
  readonly loading = signal(true);

  get justSaved(): boolean {
    return this.formService.justSaved.value > 0;
  }
  get showNextPageButton(): boolean {
    return this.step != null && !!nextFormStep[this.step];
  }
  get formsCollapsed(): boolean {
    if (this.step === DEBTOR_FORM_STEP_NAME) {
      return this.formService.debtorCollapsed();
    }
    if (this.step === CLIENT_FORM_STEP_NAME) {
      return this.formService.clientCollapsed();
    }
    return true;
  }
  get canGoNextPage(): boolean {
    return !this.hasUnsavedChanges() && !this.saving();
  }
  get showTutorial(): boolean {
    return this.formService.showTutorial;
  }
  get cameFromFeatures(): boolean {
    return this.formService.cameFromFeatures;
  }

  readonly hasUnsavedChanges = signal(false);

  ngOnInit(): void {
    this.authService.logoutOnUnauthorized = false;

    this.formService.campaign.utm_campaign =
      this.route.snapshot.queryParams.utm_campaign;
    this.formService.campaign.utm_content =
      this.route.snapshot.queryParams.utm_content;
    this.formService.campaign.utm_medium =
      this.route.snapshot.queryParams.utm_medium;
    this.formService.campaign.utm_source =
      this.route.snapshot.queryParams.utm_source;

    this.route.params
      .pipe(
        filter((params) => !this.step || params.step !== this.step),
        takeUntil(this.destroy),
      )
      .subscribe({
        next: async (params) => {
          this.canShowSpinnerTimeout = setTimeout(
            () => this.canShowSpinner.set(true),
            200,
          );
          this.loading.set(true);
          this.caseId = params.uuid;

          this.authService.logoutOnUnauthorized = false;
          let user = this.usersService.userSubject.value;
          if (!user) {
            try {
              user = await this.usersService.setUser();
            } catch {
              //
            }
          }

          if (!user) {
            // If not logged in try short token authentication
            const shortToken = this.route.snapshot.queryParams.short_token;
            if (shortToken) {
              try {
                await this.authService.loginWithShortToken(shortToken);
                user = await this.usersService.setUser();
              } catch (error) {
                console.warn('Error while logging in with short token', error);

                await this.router.navigateByUrl('login');
                return;
              }
            }
          }

          if (!user) {
            if (!this.caseId) {
              await this.formService.redirectToStep('debtor', 'guest');
              await this.openTutorial();
              return;
            } else if (this.caseId !== 'guest') {
              this.authService.setNext(this.router.url);
              this.router.navigateByUrl('login');
              return;
            }
          } else {
            if (this.caseId === 'guest') {
              this.caseId = undefined;
            }
          }

          // Create case
          if (!this.caseId) {
            if (!user) {
              await this.formService.saveCampaignLocally();
              await this.openTutorial();

              window.dataLayer?.push({
                event: 'case_initiation',
                customer_type: 'new',
              });
              return;
            }

            const client = await this.clientsService.getSelectedFormClient();

            const result = await this.casesService.createCase({
              product_type: 'hard_1',
              utm_campaign: this.formService.campaign.utm_campaign ?? undefined,
              utm_content: this.formService.campaign.utm_content ?? undefined,
              utm_medium: this.formService.campaign.utm_medium ?? undefined,
              utm_source: this.formService.campaign.utm_source ?? undefined,
            }, client.id);
            this.caseId = result.id;
            await this.formService.redirectToStep(
              DEBTOR_FORM_STEP_NAME,
              result.id,
            );
            window.dataLayer?.push({
              event: 'case_initiation',
              customer_type: 'returning',
            });
            return;
          }

          // Redirect unknown step
          if (!params.step || !formStepNames.includes(params.step)) {
            await this.formService.redirectToStep(
              DEBTOR_FORM_STEP_NAME,
              this.caseId,
            );
            return;
          }
          this.formService.setCaseId(this.caseId);

          if (this.formService.isGuest) {
            await this.formService.saveCampaignLocally();
          }

          this.step = params.step as FormStepName;
          const paymentId = this.route.snapshot.queryParams.paymentId;
          let paymentSuccess = false;

          if (this.step === PAYMENT_REDIRECT_FORM_STEP_NAME) {
            if (!paymentId) {
              await this.formService.redirectToStep(
                PAYMENT_FORM_STEP_NAME,
                this.caseId,
              );
              return;
            } else {
              const paymentResult = await this.checkPayment();
              paymentSuccess = paymentResult === 'success';
              if (!paymentSuccess) {
                await this.formService.redirectToStep(
                  PAYMENT_FORM_STEP_NAME,
                  this.caseId,
                );
                return;
              }
            }
          }

          try {
            await this.setForm();

            if (
              !this.formService.clientAllowsForm
              && this.step === PAYMENT_FORM_STEP_NAME
            ) {
              await this.formService.redirectToStep(
                CLIENT_FORM_STEP_NAME,
                this.caseId,
              );
              return;
            }

            if (this.step === DEBTOR_FORM_STEP_NAME) {
              window.dataLayer?.push({
                event: '01_debtor',
                customer_type:
                  this.formService.case?.origin === 'guest'
                    ? 'new'
                    : 'returning',
              });
            } else if (this.step === CLIENT_FORM_STEP_NAME) {
              window.dataLayer?.push({
                event: '02_client',
                customer_type:
                  this.formService.case?.origin === 'guest'
                    ? 'new'
                    : 'returning',
              });
            } else if (this.step === PAYMENT_FORM_STEP_NAME) {
              window.dataLayer?.push({
                event: '03_payment',
                customer_type:
                  this.formService.case?.origin === 'guest'
                    ? 'new'
                    : 'returning',
              });
            }
          } catch (error: AxiosError | unknown) {
            if (error instanceof AxiosError) {
              if (error.response?.status === 401) {
                return;
              }
              const notDraftError = caseNotDraftError.safeParse(
                error.response?.data,
              );
              if (notDraftError.success) {
                this.formService.case = {
                  currency_iso: '',
                  id: notDraftError.data.uuid,
                  partner_id: notDraftError.data.partner,
                  partner_logo_url: null,
                  payee_case_reference_id:
                    notDraftError.data.payee_case_reference_id,
                  type: notDraftError.data.product_type,
                  origin: notDraftError.data.origin,
                  client_id: this.clientsService.selectedClientId ?? '',
                };

                const client = this.clientsService.clientsSubject.value?.find(
                  (c) => c.id === notDraftError.data.client_id,
                );
                if (client) {
                  this.clientsService.setSelectedClient(client);
                }

                if (!notDraftError.data.gtm_start_sent_at) {
                  await this.formService.redirectAfterCaseStarted();
                } else {
                  await this.formService.redirectToCases();
                }

                return;
              }

              const notValidError = errorResponse.safeParse(
                error.response?.data,
              );
              if (notValidError.success) {
                await this.formService.redirectToStep(
                  DEBTOR_FORM_STEP_NAME,
                  this.caseId,
                );
                return;
              }

              console.error(
                'Network error while loading case',
                error.response?.data,
              );
            } else {
              console.error('Unknown error while loading case', error);
            }

            this.formService.redirectToCases();
            return;
          } finally {
            if (paymentSuccess) {
              this.formService.pushPurchaseEventToDataLayer('card');
              this.formService.redirectToCases();
            }
          }

          const jurisdictionForm =
            this.form.controls[DEBTOR_FORM_NAME]?.controls.jurisdiction;
          const queryJurisdiction =
            this.route.snapshot.queryParams.jurisdiction;
          if (jurisdictionForm && queryJurisdiction) {
            setTimeout(() => {
              jurisdictionForm?.patchValue(queryJurisdiction);
              this.router.navigate([], {
                queryParams: {
                  ...this.route.snapshot.queryParams,
                  jurisdiction: undefined,
                },
              });
            });
          }

          clearTimeout(this.canShowSpinnerTimeout);
          this.canShowSpinner.set(false);
          this.loading.set(false);
          this.showLoadingScreen.set(false);
        },
      });

    this.formService.saving.pipe(takeUntil(this.destroy)).subscribe({
      next: (value) => {
        if (value <= 0) {
          this.hasUnsavedChanges.set(false);
        }
      },
    });
  }

  ngOnDestroy(): void {
    this.authService.logoutOnUnauthorized = true;
    this.destroy.next();
    this.destroy.complete();
  }

  async openTutorial(): Promise<void> {
    await this.formService.openTutorial();
  }

  async nextPage(): Promise<void> {
    if (!this.formsCollapsed) {
      if (this.step === DEBTOR_FORM_STEP_NAME) {
        this.formService.debtorSaveRequiredError = true;
      }
      if (this.step === CLIENT_FORM_STEP_NAME) {
        this.formService.clientSaveRequiredError = true;
      }

      this.helpersService.markAllChildrenAsDirty(this.formService.form);
      return;
    }

    if (this.formService.form.invalid) {
      this.helpersService.markAllChildrenAsDirty(this.formService.form);

      const debtorForm = this.form.controls[DEBTOR_FORM_NAME];
      if (debtorForm && !debtorForm.invalid) {
        this.formService.debtorCollapsed.set(true);
      }
      return;
    }

    if (this.saving() || this.hasUnsavedChanges()) {
      return;
    }

    if (
      this.step === CLIENT_FORM_STEP_NAME
      && !this.formService.clientAllowsForm
    ) {
      return;
    }

    if (this.step === CLIENT_FORM_STEP_NAME && this.formService.isGuest) {
      await this.registerGuest();
    } else if (this.step && nextFormStep[this.step]) {
      await this.formService.redirectToStep(
        nextFormStep[this.step]!,
        this.caseId,
      );
    } else {
      console.error('Next step not found', {
        step: this.step,
        nextFormStep,
      });
    }
  }

  /**
   * @throws {never}
   */
  private async setForm(): Promise<void> {
    try {
      await this.formService.setFormData(
        (this.step as FormStepName) || DEBTOR_FORM_STEP_NAME,
      );
    } catch (error: unknown) {
      console.error('Error while setting form', error);
      this.formService.redirectToCases();
      throw error;
    }

    this.formService.setFormGroup();

    this.formService.jurisdictionChange
      .pipe(takeUntil(this.destroy), takeUntil(this.formService.formChange))
      .subscribe(async () => {
        await this.setForm();
      });

    Object.values(this.form.controls).forEach((form: AbstractControl) => {
      form.valueChanges
        .pipe(takeUntil(this.formService.formChange))
        .subscribe(() => {
          this.hasUnsavedChanges.set(form.valid);
        });
    });
    this.hasUnsavedChanges.set(false);
  }

  private async checkPayment(): Promise<'success' | 'cancel'> {
    const paymentInProgressStatuses: PaymentStatus[] = [
      'Prepared',
      'Started',
      'InProgress',
      'Waiting',
      'Reserved',
      'Authorized',
    ];

    for (let i = 0; i < 30; ++i) {
      let paymentStatus: GetCardPaymentStatus;
      try {
        const paymentId = this.route.snapshot.queryParams.paymentId;
        paymentStatus = await this.casesService.getCardPaymentStatus(
          paymentId,
          this.caseId ?? '',
        );
      } catch (error) {
        console.error('Error while getting payment status', error);
        return 'cancel';
      }

      if (paymentStatus?.status === 'Succeeded') {
        return 'success';
      } else if (!paymentInProgressStatuses.includes(paymentStatus.status)) {
        return 'cancel';
      }

      await new Promise((res) => setTimeout(res, 1000));
    }
    return 'cancel';
  }

  private async registerGuest(): Promise<void> {
    try {
      this.loading.set(true);
      this.canShowSpinnerTimeout = setTimeout(
        () => this.canShowSpinner.set(true),
        250,
      );

      await this.formService.registerGuest();
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.data?.code === 'duplicate-client') {
          const clientForm = this.form.controls[CLIENT_FORM_NAME];

          if (clientForm) {
            clientForm.setErrors({ duplicate: true });
            clientForm.markAsDirty();
          }
          return;
        }

        const validationError = errorResponse.safeParse(error.response?.data);
        if (validationError.success) {
          this.helpersService.markAllChildrenAsDirty(this.form);
          for (const [path, errors] of Object.entries(
            validationError.data.errors,
          )) {
            const control = this.form.get(path);
            const error = {
              unknown: errors[0],
            };
            control?.setErrors(error, { emitEvent: false });
          }
          return;
        }

        let message = this.translate.instant('ERROR_MESSAGES.UNKNOWN_ERROR');
        if (error.response?.data?.message) {
          message = error.response.data.message;
        }
        this.snackbar.open(message, 'OK', { duration: 10_000 });
      }

      console.error('Error while registering guest', error);
    } finally {
      clearTimeout(this.canShowSpinnerTimeout);
      this.canShowSpinner.set(false);
      this.loading.set(false);
    }
  }
}
